import * as React from 'react';
import { TbRectangle, TbLayoutRows } from 'react-icons/tb';
import Tooltip from 'components/Tooltip';
import classNames from 'styles/utils/classNames';

interface Props {
  isExpanded: boolean;
  onClick: () => void;
  /**
   * Set when the pane's active view can't fill the column. The button keeps its slot, dims, and
   * explains itself on hover or focus instead of unmounting, so the controls beside it never
   * shift when the viewer switches views. Omit it whenever the pane can expand. Ignored while
   * expanded: the way back to the split view is never disabled.
   */
  disabledReason?: string;
  className?: string;
}

/**
 * Joins view names the way the disabled tooltip reads them: "Flow, Dark Pool, and News".
 * Hand-rolled rather than Intl.ListFormat so the server and the browser agree byte for byte.
 */
const joinNames = (names: readonly string[]): string => {
  if (names.length <= 1) return names.join('');
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
};

/**
 * The reason a pane's expand toggle is disabled, built from the views that can expand so the copy
 * can't drift from the rule that gates it.
 */
export const formatExpandAvailableFor = (expandableViewNames: readonly string[]): string =>
  `Expand is available for ${joinNames(expandableViewNames)}`;

// 34px matches the widget buttons and menu triggers it shares a row with, and the 34px the pane
// content rows subtract for the header. At 36px the toggle used to grow the row and push the
// content 2px past the bottom of its pane.
const BASE_CLASSES =
  'inline-flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-md border border-palette-border bg-transparent text-gray-300 transition-colors focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary';

export default function LayoutToggleButton({
  isExpanded,
  onClick,
  disabledReason,
  className,
}: Props) {
  // An expanded pane always offers the way back, whatever its view, so it can't get stuck with the
  // other pane hidden.
  const reason = isExpanded ? undefined : disabledReason;
  const isDisabled = Boolean(reason);
  const Icon = isExpanded ? TbLayoutRows : TbRectangle;
  const label = isExpanded ? 'Return to split view' : 'Expand to full height';
  const reasonId = React.useId();

  // The Tooltip wraps the button in both states (it renders no bubble without a title), so
  // disabling and re-enabling updates the same button in place instead of remounting it, and the
  // wrapper can't change the footprint between states.
  //
  // aria-disabled rather than the disabled attribute: a natively disabled button drops out of
  // the tab order and swallows pointer events in some browsers, which would hide the tooltip
  // that explains why it's off.
  return (
    <Tooltip id={reasonId} title={reason ?? ''} position="right" variant="dark" multiline>
      <button
        type="button"
        onClick={isDisabled ? undefined : onClick}
        aria-label={label}
        aria-disabled={isDisabled || undefined}
        aria-describedby={isDisabled ? reasonId : undefined}
        className={classNames(
          BASE_CLASSES,
          isDisabled
            ? 'cursor-not-allowed opacity-40'
            : 'hover:border-gray-400 hover:bg-palette-black-1 hover:text-white',
          className,
        )}
      >
        <Icon className="text-base" />
      </button>
    </Tooltip>
  );
}
